import { useState, useMemo, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, GitBranch, Send, RotateCcw, Eye, ShieldCheck, Lightbulb, Share2 } from "lucide-react";
import { api } from "@/lib/api-client";
import { useAuthStore } from "@/store/auth.store";
import { userCan } from "@/lib/permissions";
import { useTranslation } from "@/hooks/useTranslation";
import { pickLocalized } from "@/lib/localized";
import { Button } from "@/components/ui/Button";
import { Tabs, TabsList, TabsTrigger, TabsContent, ScrollableTabsList } from "@/components/ui/Tabs";
import { StatusBadge } from "@/components/common/StatusBadge";
import { PageSkeleton } from "@/components/common/LoadingSkeleton";
import { toast } from "@/components/ui/Toast";
import { VersionSelector, type VersionOption } from "@/components/assessments/VersionSelector";
import { SecurityCommentBanner } from "@/components/assessments/SecurityCommentBanner";
import { VersioningTab, type EntityMetadata } from "@/components/assessments/VersioningTab";
import { DomainTab, type PostureWithQuestions } from "@/components/assessments/DomainTab";
import { AssessmentTab, type PostureScore } from "@/components/assessments/AssessmentTab";
import { ReportTab } from "@/components/assessments/ReportTab";
import { MitigationTab } from "@/components/assessments/MitigationTab";
import { CreateShareModal } from "@/components/sharing/CreateShareModal";
import { ActiveSharesTable } from "@/components/sharing/ActiveSharesTable";
import { ExternalResponsesTab } from "@/components/sharing/ExternalResponsesTab";
import { ApproveDialog } from "@/components/assessments/ApproveDialog";
import { RejectDialog } from "@/components/assessments/RejectDialog";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { isAnswered } from "@/components/assessments/QuestionCard";
import type { AssessmentStatus, RiskLevel, Share } from "@/types";
import type { QuestionCardProps, QuestionType, QuestionOptions } from "@/components/assessments/QuestionCard";

/** A WEIGHTED_MULTIPLE_CHOICE question with no options can never be answered
 *  (L-025); mirror the backend so it never blocks submit. */
function optionsEmpty(options: QuestionOptions | null): boolean {
  if (!options) return true;
  if (Array.isArray(options)) return options.length === 0;
  const choices = (options as { choices?: unknown[] }).choices;
  return Array.isArray(choices) ? choices.length === 0 : false;
}

// ─────────────────────────────────────────────
// AssessmentFill
//
// Screen 6 — /assessments/:id
// Full questionnaire fill view with dynamic domain tabs.
// Design: §8.5 Screen 6: Assessment Fill
// ─────────────────────────────────────────────

// API response types
interface ResponseRecord {
  id: string;
  questionId: string;
  value: unknown;
  comment: string | null;
  answeredAt: string;
}

interface QuestionRecord {
  id: string;
  code: string;
  text: string;
  textSr?: string;
  helpText?: string;
  helpTextSr?: string;
  type: QuestionType;
  options: QuestionOptions | null;
  sortOrder: number;
  domainId: string;
  postureId: string;
  required?: boolean;
  riskDimension?: string;
}

interface PostureRecord {
  id: string;
  code: string;
  name: string;
  nameSr?: string | null;
  weight: number;
  sortOrder: number;
  questions: QuestionRecord[];
}

interface DomainRecord {
  id: string;
  code: string;
  name: string;
  nameSr?: string | null;
  roleTag: string | null;
  sortOrder: number;
  postures: PostureRecord[];
}

interface ReviewCommentItem {
  id: string;
  questionId: string;
  comment: string;
  score: number | null;
  status: "NOT_STARTED" | "REVIEWED" | "NEEDS_REVISION";
}

interface AssessmentDetailResponse {
  data: {
    id: string;
    entityId: string;
    status: AssessmentStatus;
    currentVersion: number;
    createdAt: string;
    updatedAt: string;
    createdBy?: string;
    createdByName?: string;
    assignedTo?: string | null;
    metadata?: Record<string, unknown>;
    entity?: {
      id: string;
      name: string;
      metadata?: Record<string, unknown>;
      businessCriticality?: string | null;
      dataClassification?: string | null;
      applicationType?: string | null;
      development?: string | null;
      hosting?: string | null;
      department?: string | null;
    };
    schemaVersion?: {
      id: string;
      version: number;
      schema?: { name: string };
      domains?: DomainRecord[];
    };
    responses?: ResponseRecord[];
    reviewComments?: ReviewCommentItem[];
    reviewComment?: {
      comment: string;
      reviewer?: string;
      reviewedAt?: string;
    } | null;
    latestScore?: {
      overallScore: number;
      overallLevel: RiskLevel;
      postureScores?: PostureScore[];
    } | null;
    responseSummary?: { total: number; answered: number; completionPct: number };
    versions?: VersionOption[];
    approvedBy?: string;
  };
}

// Local state for responses before sending to backend
interface LocalResponses {
  [questionId: string]: { value: unknown; comment: string | null };
}

function buildResponseMap(responses: ResponseRecord[]): LocalResponses {
  const map: LocalResponses = {};
  for (const r of responses) {
    map[r.questionId] = { value: r.value, comment: r.comment };
  }
  return map;
}

// S-098: the VersionSelector switches versions by navigating to a new :id on the
// SAME route, so React keeps this component mounted and its per-assessment state
// (localResponses, activeTab — and children's, e.g. VersioningTab's `form` seeded
// once from props) would leak from the previous version onto the next. Keying the
// inner component on the id remounts the whole subtree on every version switch, so
// all local state resets cleanly (and DomainTab's WP08 flush re-registers fresh).
export function AssessmentFill() {
  const { id } = useParams<{ id: string }>();
  return <AssessmentFillInner key={id ?? "new"} />;
}

function AssessmentFillInner() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const { t, language } = useTranslation();
  // S-121: the workflow endpoints require the CASL review/approve ability —
  // gate the buttons by the user's real abilities (works for custom roles too).
  const canReview = userCan(user, "review", "Assessment");

  const [activeTab, setActiveTab] = useState("versioning");
  // S-105: which edge (first/last posture) the next-mounted domain tab opens at.
  // "last" only when navigating BACK a domain, so "Previous Section" lands on the
  // previous domain's LAST posture instead of resetting to its first.
  const [entryEdge, setEntryEdge] = useState<"first" | "last">("first");
  // S-113: true while the Versioning tab holds unsaved metadata edits. Radix
  // unmounts the tab content on switch, which silently destroyed the un-saved
  // form — every exit path must go through confirmDiscardMeta() first.
  const [metaDirty, setMetaDirty] = useState(false);
  // One physical click on a tab trigger can invoke this twice (Radix requests
  // activation on focus AND on click while the controlled value stays put), so
  // a declined confirm immediately re-prompted. Reuse the answer briefly.
  const lastDiscardAnswer = useRef<{ at: number; ok: boolean } | null>(null);
  const confirmDiscardMeta = () => {
    if (!metaDirty) return true;
    const last = lastDiscardAnswer.current;
    if (last && Date.now() - last.at < 500) return last.ok;
    const ok = window.confirm(
      t("assessment.discardMetadataConfirm"),
    );
    lastDiscardAnswer.current = { at: Date.now(), ok };
    if (ok) setMetaDirty(false);
    return ok;
  };
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [localResponses, setLocalResponses] = useState<LocalResponses>({});

  // S-094: the mounted DomainTab registers its awaitable autosave flush here so
  // Submit can drain the debounced queue before the server snapshots the version.
  const activeFlushRef = useRef<null | (() => Promise<void>)>(null);
  const registerDomainFlush = useCallback((flush: (() => Promise<void>) | null) => {
    activeFlushRef.current = flush;
  }, []);

  // Domain-scoped fill permission: returns true if the current user can fill this domain.
  // Falls back to allowing everything when fillableDomainTags is not yet set.
  function canFillDomain(domainRoleTag: string | null): boolean {
    if (!user?.fillableDomainTags) return true;
    if (user.fillableDomainTags.includes("ALL")) return true;
    return domainRoleTag ? user.fillableDomainTags.includes(domainRoleTag) : false;
  }

  const { data, isLoading, error } = useQuery<AssessmentDetailResponse>({
    queryKey: ["assessment", id],
    queryFn: () => api.get(`/assessments/${id}?include=domains,responses,versions,score`),
    enabled: !!id,
  });

  const assessment = data?.data;

  // Merge API responses with local changes
  const responseMap = useMemo(() => {
    const base = buildResponseMap(assessment?.responses ?? []);
    return { ...base, ...localResponses };
  }, [assessment?.responses, localResponses]);

  // Build map of questionId -> review comment for NEEDS_REVISION questions
  const rejectedQuestionMap = useMemo(() => {
    const map: Record<string, ReviewCommentItem> = {};
    for (const rc of assessment?.reviewComments ?? []) {
      if (rc.status === "NEEDS_REVISION") {
        map[rc.questionId] = rc;
      }
    }
    return map;
  }, [assessment?.reviewComments]);

  function handleResponseChange(questionId: string, value: unknown, comment: string | null) {
    setLocalResponses((prev) => ({ ...prev, [questionId]: { value, comment } }));
  }

  // Workflow mutations
  const submitMutation = useMutation({
    mutationFn: () => api.post(`/assessments/${id}/submit`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["assessment", id] });
      toast.success(t("assessment.submittedForReview"));
    },
    onError: (err: Error) => toast.error(err.message ?? t("assessment.failedToSubmit")),
  });

  const unsubmitMutation = useMutation({
    mutationFn: () => api.post(`/assessments/${id}/unsubmit`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["assessment", id] });
      toast.success(t("assessment.returnedToDraft"));
    },
    onError: (err: Error) => toast.error(err.message ?? t("assessment.failedToUnsubmit")),
  });

  const startReviewMutation = useMutation({
    mutationFn: () => api.post(`/assessments/${id}/start-review`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["assessment", id] });
      toast.success(t("assessment.movedToInReview"));
    },
    onError: (err: Error) => toast.error(err.message ?? t("assessment.failedToStartReview")),
  });

  const reviseMutation = useMutation({
    mutationFn: () => api.post(`/assessments/${id}/revise`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["assessment", id] });
      toast.success(t("assessment.revisionCreated"));
    },
    onError: (err: Error) => toast.error(err.message ?? t("assessment.failedToCreateRevision")),
  });

  const newVersionMutation = useMutation({
    mutationFn: () => api.post<{ data: { id: string } }>(`/assessments/${id}/fork`),
    onSuccess: (res) => {
      toast.success(t("assessment.newVersionCreated"));
      navigate(`/assessments/${res.data.id}`);
    },
    onError: (err: Error) => toast.error(err.message ?? t("assessment.failedToCreateVersion")),
  });

  // Shares feeding the Sharing host tab (ActiveSharesTable + ExternalResponsesTab
  // read the same ['assessment-shares', id] cache entry).
  const { data: sharesData, isLoading: sharesLoading } = useQuery<{ data: Share[] }>({
    queryKey: ["assessment-shares", id],
    queryFn: () => api.get(`/shares?assessmentId=${id}`),
    enabled: !!id,
  });

  if (isLoading) return <PageSkeleton />;

  if (error || !assessment) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <p className="text-[16px] text-text-primary">{t("assessment.notFound")}</p>
        <Button variant="ghost" onClick={() => navigate("/assessments")} className="mt-4">
          {t("assessment.backToAssessments")}
        </Button>
      </div>
    );
  }

  const status = assessment.status;
  const isReadOnly = status !== "DRAFT";
  const domains = assessment.schemaVersion?.domains ?? [];
  const shares = sharesData?.data ?? [];
  const versions = (assessment.versions ?? []).map((v) => {
    const raw = v as VersionOption & { currentVersion?: number };
    return { ...raw, version: raw.currentVersion ?? raw.version };
  });
  const reviewComment = assessment.reviewComment;
  // Build metadata for VersioningTab:
  // 1. Start with entity top-level columns (businessCriticality, dataClassification, etc.) as defaults.
  // 2. Overlay entity.metadata JSON (free-form fields like businessSponsor).
  // 3. Overlay assessment-level metadata overrides last (per-assessment values take precedence).
  const entityDefaults: Partial<EntityMetadata> = {
    businessCriticality: assessment.entity?.businessCriticality as EntityMetadata["businessCriticality"] ?? undefined,
    dataClassification: assessment.entity?.dataClassification as EntityMetadata["dataClassification"] ?? undefined,
    department: assessment.entity?.department ?? undefined,
  };
  const metadata = {
    ...entityDefaults,
    ...(assessment.entity?.metadata ?? {}),
    ...(assessment.metadata ?? {}),
  } as EntityMetadata;
  // S-102/L-047: derive completion live from the reactive responseMap (server
  // responses + local edits) so the header %, the Risk Overview gate and the
  // Submit gate all update as the user answers — no page reload needed.
  // Plain (non-hook) computations — must stay below the early returns above
  // without useMemo to respect the Rules of Hooks.
  const allQuestions = domains.flatMap((d) => d.postures.flatMap((p) => p.questions));
  const completion = (() => {
    let answeredTotal = 0;
    let scoreableAnswered = 0;
    const requiredUnanswered: string[] = [];
    for (const q of allQuestions) {
      const done = isAnswered(q.type, responseMap[q.id]?.value);
      if (done) answeredTotal += 1;
      if (done && q.type !== "OPEN_ENDED" && q.riskDimension !== "INFORMATIONAL") scoreableAnswered += 1;
      const answerable = !(q.type === "WEIGHTED_MULTIPLE_CHOICE" && optionsEmpty(q.options));
      if (q.required !== false && !done && answerable) requiredUnanswered.push(q.code);
    }
    const total = allQuestions.length;
    return {
      pct: total > 0 ? Math.round((answeredTotal / total) * 100) : 0,
      scoreableAnswered,
      requiredUnanswered,
    };
  })();
  const completionPct = completion.pct;
  // Mirror the backend WP06 gate: ≥1 scoreable answered AND every answerable required question answered.
  const canSubmit = completion.scoreableAnswered >= 1 && completion.requiredUnanswered.length === 0;

  // Sorted domains (used for tabs and content)


  // Sorted domains
  const sortedDomains = [...domains].sort((a, b) => a.sortOrder - b.sortOrder);

  // Build postures for active domain tab
  function buildPosturesForDomain(domain: DomainRecord): PostureWithQuestions[] {
    return [...domain.postures]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((posture) => ({
        postureId: posture.id,
        postureName: posture.name,
        postureNameSr: posture.nameSr ?? undefined,
        postureCode: posture.code,
        questions: [...posture.questions]
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((q): Omit<QuestionCardProps, "index"> => ({
            questionId: q.id,
            code: q.code,
            text: q.text,
            textSr: q.textSr,
            helpTextSr: q.helpTextSr,
            helpText: q.helpText,
            type: q.type,
            options: q.options,
            value: responseMap[q.id]?.value ?? null,
            comment: responseMap[q.id]?.comment ?? null,
            readOnly: isReadOnly,
            onValueChange: (qId, val) => handleResponseChange(qId, val, responseMap[qId]?.comment ?? null),
            onCommentChange: (qId, c) => handleResponseChange(qId, responseMap[qId]?.value ?? null, c),
          })),
      }));
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-4">
      {/* Page header */}
      <div className="flex flex-shrink-0 items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <button
            onClick={() => {
              // S-113: leaving the page discards any unsaved metadata edits.
              if (confirmDiscardMeta()) navigate("/assessments");
            }}
            className="mt-1 flex h-7 w-7 items-center justify-center rounded-control text-text-muted hover:bg-hover hover:text-text-secondary transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-[24px] font-semibold text-text-primary">
                {assessment.entity?.name ?? t("assessment.fallbackName")}
              </h1>
              <StatusBadge type="assessment" status={status} />
              {assessment.schemaVersion?.schema?.name && (
                <span
                  className="rounded-badge px-2 py-0.5 text-[12px] font-semibold"
                  style={{
                    backgroundColor: "var(--accent-bg)",
                    color: "var(--accent-text)",
                  }}
                >
                  {assessment.schemaVersion.schema.name}
                </span>
              )}
            </div>
            {/* Progress bar */}
            <div className="flex items-center gap-2">
              <div className="h-1.5 w-48 overflow-hidden rounded-full" style={{ backgroundColor: "var(--bg-hover)" }}>
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${completionPct}%`,
                    backgroundColor: completionPct >= 100 ? "var(--success)" : "var(--accent)",
                  }}
                />
              </div>
              <span className="text-[12px] text-text-muted">
                {Math.round(completionPct)}% {t("assessment.completeSuffix")}
              </span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {/* Version selector */}
          {versions.length > 1 && (
            <VersionSelector
              versions={versions}
              currentId={id!}
              onSelect={(vId) => {
                // S-113: switching versions remounts the whole page (S-098 key),
                // which would silently discard unsaved metadata edits.
                if (confirmDiscardMeta()) navigate(`/assessments/${vId}`);
              }}
            />
          )}

          {/* Workflow actions */}
          {status === "DRAFT" && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(`/assessments/${id}/review`)}
                leadingIcon={<Eye className="h-4 w-4" />}
              >
                {t("assessment.previewReview")}
              </Button>
              {!canSubmit && completion.requiredUnanswered.length > 0 && (
                <span className="self-center text-[12px] text-text-muted">
                  {completion.requiredUnanswered.length} {t("assessment.requiredUnansweredSuffix")}
                </span>
              )}
              <Button
                size="sm"
                onClick={() => setSubmitOpen(true)}
                isLoading={submitMutation.isPending}
                disabled={!canSubmit}
                leadingIcon={<Send className="h-4 w-4" />}
              >
                {t("assessment.submitForReview")}
              </Button>
            </>
          )}

          {status === "SUBMITTED" && canReview && (
            <>
              <Button
                size="sm"
                onClick={() => startReviewMutation.mutate()}
                isLoading={startReviewMutation.isPending}
                style={{ backgroundColor: "var(--accent)" }}
              >
                {t("assessment.startReview")}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => unsubmitMutation.mutate()}
                isLoading={unsubmitMutation.isPending}
                style={{ backgroundColor: "var(--bg-hover)" }}
              >
                {t("assessment.unsubmit")}
              </Button>
            </>
          )}

          {status === "IN_REVIEW" && canReview && (
            <>
              <Button
                variant="outline"
                size="sm"
                style={{ borderColor: "var(--danger)", color: "var(--danger)" }}
                onClick={() => setRejectOpen(true)}
              >
                {t("common.reject")}
              </Button>
              <Button
                size="sm"
                style={{ backgroundColor: "var(--success)" }}
                onClick={() => setApproveOpen(true)}
              >
                {t("common.approve")}
              </Button>
            </>
          )}

          {status === "REJECTED" && (
            <Button
              size="sm"
              onClick={() => reviseMutation.mutate()}
              isLoading={reviseMutation.isPending}
              leadingIcon={<RotateCcw className="h-4 w-4" />}
            >
              {t("assessment.revise")}
            </Button>
          )}

          {/* Create new version — always available */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => newVersionMutation.mutate()}
            isLoading={newVersionMutation.isPending}
            leadingIcon={<GitBranch className="h-4 w-4" />}
          >
            {t("assessment.newVersion")}
          </Button>
        </div>
      </div>

      {/* Security comment banner */}
      {reviewComment && (
        <SecurityCommentBanner
          comment={reviewComment.comment}
          reviewer={reviewComment.reviewer}
          reviewedAt={reviewComment.reviewedAt}
        />
      )}

      {/* Tab bar — fills remaining height; content scrolls internally so sticky
          rails (e.g. the Sections sidebar) pin to the visible viewport. */}
      <Tabs
        value={activeTab}
        onValueChange={(v) => {
          // S-113: leaving the Versioning tab unmounts it — block the switch
          // unless unsaved metadata edits are saved or explicitly discarded.
          if (activeTab === "versioning" && v !== "versioning" && !confirmDiscardMeta()) {
            return;
          }
          // S-105: a manual tab click always enters at the first posture. Radix
          // fires this only for user trigger clicks, NOT for the programmatic
          // setActiveTab used by prev/next nav, so the "last" edge set in
          // onPrevDomain survives the remount it triggers.
          setEntryEdge("first");
          setActiveTab(v);
        }}
        className="flex min-h-0 flex-1 flex-col"
      >
        {/* Large schemas produce many domain tabs — keep them on ONE row with
            horizontal scroll (edge fades + chevrons) instead of wrapping. */}
        <ScrollableTabsList activeValue={activeTab} className="flex-shrink-0">
          <TabsList className="flex w-max flex-nowrap items-center h-auto gap-1 p-1">
            <TabsTrigger value="versioning" className="font-semibold gap-1.5">
              <GitBranch className="h-3.5 w-3.5" />
              {t("assessment.tabs.versioning")}
            </TabsTrigger>
            {sortedDomains.map((domain) => {
              const domainLabel = pickLocalized(domain.name, domain.nameSr, language);
              return (
                <TabsTrigger
                  key={domain.id}
                  value={`domain-${domain.id}`}
                  className="max-w-[230px]"
                  title={domainLabel}
                >
                  <span className="truncate">{domainLabel}</span>
                </TabsTrigger>
              );
            })}
            <TabsTrigger value="assessment" className="font-semibold gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5" />
              {t("assessment.tabs.riskOverview")}
            </TabsTrigger>
            <TabsTrigger value="mitigation" className="font-semibold gap-1.5">
              <Lightbulb className="h-3.5 w-3.5" />
              {t("assessment.tabs.mitigation")}
            </TabsTrigger>
            <TabsTrigger value="sharing" className="font-semibold gap-1.5">
              <Share2 className="h-3.5 w-3.5" />
              {t("assessment.tabs.sharing")}
            </TabsTrigger>
          </TabsList>
        </ScrollableTabsList>

        {/* Versioning tab */}
        <TabsContent value="versioning" className="mt-5 flex-1 min-h-0">
          <VersioningTab
            assessmentId={id!}
            entityId={assessment.entityId}
            status={status}
            metadata={metadata}
            createdAt={assessment.createdAt}
            updatedAt={assessment.updatedAt}
            version={assessment.currentVersion}
            documentStatus={status}
            createdBy={assessment.createdByName ?? assessment.createdBy}
            approvedBy={assessment.approvedBy}
            onDirtyChange={setMetaDirty}
          />
        </TabsContent>

        {/* Risk Assessment tab (merged Assessment + Report) */}
        <TabsContent value="assessment" className="mt-5 flex-1 min-h-0">
          <AssessmentTab
            assessmentId={id!}
            overallScore={assessment.latestScore?.overallScore ?? null}
            riskLevel={assessment.latestScore?.overallLevel ?? null}
            postureScores={assessment.latestScore?.postureScores ?? []}
            completionPct={completionPct}
            version={assessment.currentVersion}
            status={status}
            createdAt={assessment.createdAt}
            updatedAt={assessment.updatedAt}
            schemaName={assessment.schemaVersion?.schema?.name}
            entityName={assessment.entity?.name}
            assignedTo={assessment.assignedTo}
          />
          <div className="mt-6">
            <ReportTab
              assessmentId={id!}
              status={status}
            />
          </div>
        </TabsContent>

        {/* Mitigation tab */}
        <TabsContent value="mitigation" className="mt-5 flex-1 min-h-0">
          <MitigationTab assessmentId={id!} />
        </TabsContent>

        {/* Sharing tab */}
        <TabsContent value="sharing" className="mt-5 flex-1 min-h-0">
          <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between gap-3">
              <div className="flex flex-col gap-1">
                <h2 className="text-[18px] font-semibold text-text-primary">{t("assessment.tabs.sharing")}</h2>
                <p className="text-[13px] text-text-muted">
                  {t("assessment.sharingSubtitle")}
                </p>
              </div>
              <Button
                size="sm"
                onClick={() => setShareModalOpen(true)}
                leadingIcon={<Share2 className="h-4 w-4" />}
              >
                {t("assessment.createShare")}
              </Button>
            </div>

            <ActiveSharesTable assessmentId={id!} shares={shares} isLoading={sharesLoading} />

            <div className="flex flex-col gap-2">
              <h3 className="text-[15px] font-semibold text-text-primary">{t("assessment.externalResponses")}</h3>
              <ExternalResponsesTab assessmentId={id!} />
            </div>
          </div>
        </TabsContent>

        {/* Domain tabs */}
        {sortedDomains.map((domain) => {
          const domainReadOnly = isReadOnly || !canFillDomain(domain.roleTag);
          const domainIdx = sortedDomains.findIndex((d) => d.id === domain.id);

          // Collect rejected questions in this domain (for revision display)
          const domainRejectedComments = domain.postures
            .flatMap((p) => p.questions)
            .flatMap((q) => {
              const review = rejectedQuestionMap[q.id];
              return review ? [{ question: q, review }] : [];
            });
          const showRevisionBanners = status === "DRAFT" && domainRejectedComments.length > 0;

          return (
            <TabsContent key={domain.id} value={`domain-${domain.id}`} className="mt-5 flex-1 min-h-0">
              {showRevisionBanners && (
                <div className="mb-4 flex flex-col gap-2">
                  <p
                    className="text-[12px] font-semibold uppercase tracking-[1px]"
                    style={{ color: "var(--warning)" }}
                  >
                    {domainRejectedComments.length} {t(domainRejectedComments.length !== 1 ? "assessment.questionsNeedRevisionPlural" : "assessment.questionNeedsRevisionSingular")}
                  </p>
                  {domainRejectedComments.map(({ question, review }) => (
                    <div
                      key={question.id}
                      className="rounded-card border px-4 py-3"
                      style={{
                        backgroundColor: "var(--warning-bg, color-mix(in srgb, var(--warning, #f59e0b) 10%, transparent))",
                        borderColor: "var(--warning-border, color-mix(in srgb, var(--warning, #f59e0b) 30%, transparent))",
                      }}
                    >
                      <p className="text-[13px] font-medium text-text-primary">
                        {question.code}: {question.text}
                      </p>
                      {review.comment && (
                        <p className="mt-1 text-[13px] text-text-secondary">
                          {t("assessment.reviewerLabel")}: {review.comment}
                        </p>
                      )}
                      {review.score !== null && review.score !== undefined && (
                        <p className="mt-0.5 text-[12px] text-text-muted">
                          {t("assessment.score")}: {review.score}/5
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <DomainTab
                assessmentId={id!}
                postures={buildPosturesForDomain(domain)}
                responses={responseMap}
                readOnly={domainReadOnly}
                showReadOnlyBanner={!isReadOnly && !canFillDomain(domain.roleTag)}
                onResponseChange={handleResponseChange}
                onRegisterFlush={registerDomainFlush}
                currentDomainIndex={domainIdx}
                totalDomains={sortedDomains.length}
                initialPosture={entryEdge}
                onPrevDomain={() => {
                  const prev = sortedDomains[domainIdx - 1];
                  if (prev) {
                    setEntryEdge("last");
                    setActiveTab(`domain-${prev.id}`);
                    document.querySelector("main")?.scrollTo({ top: 0 });
                  }
                }}
                onNextDomain={() => {
                  const next = sortedDomains[domainIdx + 1];
                  if (next) {
                    setEntryEdge("first");
                    setActiveTab(`domain-${next.id}`);
                    document.querySelector("main")?.scrollTo({ top: 0 });
                  }
                }}
              />
            </TabsContent>
          );
        })}
      </Tabs>

      {/* Approve / Reject dialogs */}
      <ApproveDialog
        open={approveOpen}
        onOpenChange={setApproveOpen}
        assessmentId={id!}
      />
      <RejectDialog
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        assessmentId={id!}
      />
      <CreateShareModal
        assessmentId={id!}
        open={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
      />

      {/* Submit is irreversible until returned to draft — confirm first (L-033). */}
      <ConfirmDialog
        open={submitOpen}
        onOpenChange={setSubmitOpen}
        isDestructive={false}
        title={t("assessment.submitConfirmTitle")}
        description={t("assessment.submitConfirmDesc")}
        confirmLabel={t("common.submit")}
        isLoading={submitMutation.isPending}
        onConfirm={async () => {
          // S-094: drain the debounced autosave BEFORE the server snapshots the
          // immutable version, so the last-answered questions can't be lost from
          // the submitted, hashed snapshot. If the save fails we do NOT submit.
          try {
            await activeFlushRef.current?.();
          } catch {
            toast.error(t("assessment.autoSaveBeforeSubmitFailed"));
            return;
          }
          setSubmitOpen(false);
          submitMutation.mutate();
        }}
      />
    </div>
  );
}
